import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { TRAFFIC, normaliseIssue } from '@/lib/trafficLight'
import { cn } from '@/lib/utils'

function TrafficDot({ dot, className }) {
  return (
    <span
      className={cn('inline-block shrink-0 rounded-full', dot, className)}
      aria-hidden
    />
  )
}

function issueToneClass(status) {
  if (status === 'red') return 'text-destructive'
  if (status === 'amber') return 'text-amber-700 dark:text-amber-400'
  return 'text-muted-foreground'
}

function IssueLine({ issue }) {
  const traffic = TRAFFIC[issue.status] ?? TRAFFIC.grey
  return (
    <li className={cn('flex items-start gap-1.5', issueToneClass(issue.status))}>
      <TrafficDot dot={traffic.dot} className="mt-1 h-1.5 w-1.5" />
      <span>{issue.text}</span>
    </li>
  )
}

function SectionIndicator({ section, onClick }) {
  const traffic = TRAFFIC[section.status] ?? TRAFFIC.grey
  const clickable = Boolean(onClick)
  const issues = (section.issues ?? [])
    .map((issue) => normaliseIssue(issue, section.status))
    .filter(Boolean)

  return (
    <div
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={clickable ? () => onClick(section.id) : undefined}
      onKeyDown={
        clickable
          ? (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                onClick(section.id)
              }
            }
          : undefined
      }
      className={cn(
        'flex items-start gap-3 rounded-lg border border-border p-3',
        section.status === 'red' && 'border-destructive/40 bg-destructive/5',
        section.status === 'amber' && 'border-amber-500/30 bg-amber-500/5',
        section.status === 'green' && 'border-emerald-500/20 bg-emerald-500/5',
        clickable && 'cursor-pointer transition-colors hover:bg-muted/30',
      )}
    >
      <TrafficDot dot={traffic.dot} className="mt-1 h-3 w-3" />
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-sm font-medium">{section.title}</p>
          <Badge variant={section.badge}>{section.statusLabel}</Badge>
        </div>
        {issues.length > 0 ? (
          <ul className="space-y-0.5 text-xs">
            {issues.map((issue) => (
              <IssueLine key={`${issue.status}:${issue.text}`} issue={issue} />
            ))}
          </ul>
        ) : (
          <p className="text-xs text-muted-foreground">No issues flagged</p>
        )}
      </div>
    </div>
  )
}

export function TrafficLightSummary({ title, posture, onSectionClick }) {
  const visible = posture?.sections?.filter((s) => s.status !== 'grey') ?? []
  if (!visible.length) return null

  const { overall } = posture

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center gap-3">
          <TrafficDot dot={overall.dot} className="h-4 w-4" />
          <div className="flex-1 min-w-0">
            <CardTitle className="text-base">{title}</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">{overall.headline}</p>
          </div>
          <Badge variant={overall.badge} className="shrink-0">
            {overall.label}
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
          {visible.map((section) => (
            <SectionIndicator
              key={section.id}
              section={section}
              onClick={onSectionClick}
            />
          ))}
        </div>
      </CardContent>
    </Card>
  )
}
